// Shared v1 API client: bearer tokens from OIDC, Idempotency-Key on every mutating request,
// RFC 7807 problem details surfaced as `error.problem`, and login redirect on 401.
import axios from 'axios';
import config from '../config';
import {
  clearSignedIn,
  forgetIdentitySession,
  getAccessToken,
  hasLegacySession,
  login,
  signedInRecently,
} from '../auth/oidc';
import {clearSession} from '../auth/legacy';
import {SESSION_ENDED_QUERY} from '../auth/sessionNotice';

const MUTATING = new Set(['post', 'put', 'patch', 'delete']);

/** Leaves the protected page for /login with the neutral "session ended" notice. */
const toLoginSessionEnded = () => {
  if (window.location.pathname !== '/login') {
    window.location.assign(`/login${SESSION_ENDED_QUERY}`);
  }
};

export const newIdempotencyKey = () =>
  window.crypto && window.crypto.randomUUID
    ? window.crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

/**
 * Request config that pins the Idempotency-Key of one user action, so a retry of that action
 * carries the same key. Without a key the interceptor generates a fresh one (the default).
 */
export const withIdempotencyKey = (idempotencyKey) =>
  idempotencyKey ? {headers: {'Idempotency-Key': idempotencyKey}} : undefined;

const api = axios.create({
  baseURL: `${config.apiBaseUrl}/api/v1`,
  headers: {Accept: 'application/json'},
});

api.interceptors.request.use(async (req) => {
  const token = await getAccessToken();
  if (token) req.headers.Authorization = `Bearer ${token}`;
  if (MUTATING.has((req.method || 'get').toLowerCase()) && !req.headers['Idempotency-Key']) {
    req.headers['Idempotency-Key'] = newIdempotencyKey();
  }
  return req;
});

api.interceptors.response.use(
  (res) => {
    clearSignedIn();
    return res;
  },
  (err) => {
    const res = err.response;
    if (res && res.data && typeof res.data === 'object' && res.data.type) {
      err.problem = res.data;
      err.message = res.data.detail || res.data.title || err.message;
    }
    if (res && res.status === 401 && hasLegacySession()) {
      // A first-party token was rejected (expired, revoked, or the account deactivated): drop it
      // and ask for credentials. The notice does not say which of those happened.
      clearSession();
      toLoginSessionEnded();
      return new Promise(() => {});
    }
    if (res && res.status === 401) {
      if (signedInRecently()) {
        // The token was refused right after an identity provider sign-in completed (for example,
        // a deactivated account). Stop: drop the OIDC session and show the login page rather than
        // sending the browser back to the identity provider again.
        clearSignedIn();
        forgetIdentitySession().finally(toLoginSessionEnded);
        return new Promise(() => {});
      }
      if (!window.location.pathname.startsWith('/auth/')) {
        login(window.location.pathname + window.location.search);
        // Halt the chain: the browser is navigating to the identity provider.
        return new Promise(() => {});
      }
    }
    return Promise.reject(err);
  },
);

/** First field error message for a form field, if the API rejected it. */
export const fieldError = (err, field) => {
  const errors = (err && err.problem && err.problem.errors) || [];
  const match = errors.find((e) => e.field === field);
  return match ? match.message : null;
};

/** Unwraps paginated envelopes into a plain array for list views. */
export const items = (data) => (Array.isArray(data) ? data : (data && data.items) || []);

export default api;

/** Human-readable message for a failed request, including field errors. */
export const problemMessage = (err, fallback = 'Something went wrong. Please try again.') => {
  const p = err && err.problem;
  if (!p) return (err && err.message) || fallback;
  const fields = (p.errors || []).map((e) => `${e.field}: ${e.message}`);
  return [p.detail || p.title, ...fields].filter(Boolean).join('\n') || fallback;
};
